/**
 * Google Tag Manager Event Utilities
 *
 * This module provides type-safe functions for pushing events to GTM's dataLayer.
 * All events are strongly typed to prevent runtime errors and ensure consistency.
 * For every tracking initiative there is an extra handler module that bundles
 * all push functions for that concern.
 *
 * All event types are defined in ./types.ts and exported via ./index.ts.
 *
 * USAGE:
 * - Always use the convenience functions (pushAuthEvent, pushUserId, etc.)
 * - NEVER use `window.dataLayer.push()` directly or `window as any`
 * - All event tracking should go through the push functions in this module
 *   or the handler submodules
 *
 * TO ADD A NEW EVENT TYPE:
 * 1. Define the event type interface in types.ts
 * 2. Add it to the appropriate union type (or GTMEvent if it's a new category)
 * 3. Create a convenience push function in the relevant handler module
 * 4. Export the function from index.ts
 * 5. Use the convenience function in your components
 */
import { TEcommerceEvents } from '../events/ecommerce/ecommerceTypes';
import type {
	TAppInfoEvent,
	TDataLayerEvent,
	TGTMEvent,
} from '../events/types';

/**
 * Module augmentation: Add GTM dataLayer to the browser's Window type.
 * This allows TypeScript to recognize window.dataLayer without errors.
 * Required because we're in strict mode and dataLayer is added by GTM script at runtime.
 */
declare global {
	interface Window {
		dataLayer: TDataLayerEvent[];
	}
}

/**
 * Type-safe function to push events to the GTM dataLayer.
 * Use this directly for maximum flexibility, or use the convenience functions below.
 */
export const pushEvent = (eventData: TGTMEvent): void => {
	window.dataLayer = window.dataLayer || [];
	if (isEcommerceEvent(eventData)) {
		window.dataLayer.push({ ecommerce: null }); //Reset in case of e-comm events.
	}
	window.dataLayer.push(eventData);
};

// Convenience functions for common events

export const pushUserId = (userId: string | null): void => {
	pushEvent({ event: 'user_id_set', user_id: userId });
};

export const pushAppInfo = (info: Omit<TAppInfoEvent, 'event'>): void => {
	pushEvent({ event: 'app_info_set', ...info });
};

function isEcommerceEvent(obj: unknown): obj is TEcommerceEvents {
	return typeof obj === 'object' && obj !== null && 'ecommerce' in obj;
}
